import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Edit, ExternalLink } from 'lucide-react';
import { SidebarLayout } from '../components/layout';
import { Button } from '../components/ui/button';
import { Tabs } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { WorkflowAuditTrail } from '../components/workflow/workflow-audit-trail';
import { useDivisionWorkflow } from '../hooks/use-division-workflows';
import { useDivisionRoles } from '../hooks/use-division-roles';
import { useDivisionUsers } from '../hooks/use-division-users';
import { useWorkflowOrderAttributes } from '../hooks/use-order-attributes';
import { useOrders } from '../hooks/use-orders';
import { useAuth } from '../hooks/use-auth';
import { DivisionRole, UserDto } from '../types';
import { formatCurrency, formatDate, getStepTypeLabel, sortableHeaderProps } from '../utils';

export function WorkflowViewPage() {
  const { divisionId, workflowId } = useParams<{ divisionId: string; workflowId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'basic');
  const { isAuthenticated, isSupport } = useAuth();

  // Orders filter state
  const [ordersFilters, setOrdersFilters] = useState(() => ({
    page: parseInt(searchParams.get('orderPage') || '1'),
    limit: parseInt(searchParams.get('orderLimit') || '50'),
    search: searchParams.get('orderSearch') || '',
    status: searchParams.get('orderStatus') || '',
    sortBy: searchParams.get('orderSortBy') || 'CreatedAt',
    sortOrder: (searchParams.get('orderSortOrder') || 'desc') as 'asc' | 'desc',
  }));
  const [ordersSearchInput, setOrdersSearchInput] = useState(ordersFilters.search);

  useEffect(() => {
    const timer = setTimeout(() => {
      setOrdersFilters(prev => ({ ...prev, search: ordersSearchInput, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [ordersSearchInput]);

  useEffect(() => {
    if (activeTab === 'orders') {
      const params = new URLSearchParams(searchParams);
      params.set('tab', 'orders');
      params.set('orderPage', ordersFilters.page.toString());
      params.set('orderLimit', ordersFilters.limit.toString());
      if (ordersFilters.search) params.set('orderSearch', ordersFilters.search);
      else params.delete('orderSearch');
      if (ordersFilters.status) params.set('orderStatus', ordersFilters.status);
      else params.delete('orderStatus');
      params.set('orderSortBy', ordersFilters.sortBy);
      params.set('orderSortOrder', ordersFilters.sortOrder);
      setSearchParams(params, { replace: true });
    }
  }, [activeTab, ordersFilters, setSearchParams]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  // Data fetching
  const { data: workflow, isLoading } = useDivisionWorkflow(divisionId as string, workflowId as string);
  const { data: rolesResponse } = useDivisionRoles(divisionId as string);
  const { data: usersResponse } = useDivisionUsers(divisionId as string, { active: true, limit: 1000 });
  const { data: orderAttributesResponse, isLoading: isLoadingFields } = useWorkflowOrderAttributes(workflowId as string);
  const { data: ordersResponse, isLoading: isLoadingOrders } = useOrders(divisionId as string, {
    workflowId,
    page: ordersFilters.page,
    limit: ordersFilters.limit,
    search: ordersFilters.search || undefined,
    status: ordersFilters.status || undefined,
    sortBy: ordersFilters.sortBy,
    sortOrder: ordersFilters.sortOrder,
  });

  const roles: DivisionRole[] = rolesResponse?.roles || [];
  const users: UserDto[] = usersResponse?.users || [];

  const getRoleName = (id: string) => roles.find(r => r.id === id)?.name ?? id;
  const getUserLabel = (id: string) => {
    const u = users.find(u => u.id === id);
    return u ? `${u.firstName} ${u.lastName} (${u.email})` : id;
  };

  const handleTabChange = (tabId: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', tabId);
    if (tabId !== 'orders') {
      params.delete('orderPage');
      params.delete('orderLimit');
      params.delete('orderSearch');
      params.delete('orderStatus');
      params.delete('orderSortBy');
      params.delete('orderSortOrder');
    }
    setSearchParams(params, { replace: true });
    setActiveTab(tabId);
  };

  const handleOrdersSort = (field: string) => {
    setOrdersFilters(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc',
      page: 1,
    }));
  };

  const getOrdersSortIcon = (field: string) => {
    if (ordersFilters.sortBy !== field) return <i className="fa-solid fa-sort text-gray-500 dark:text-secondary-400 ml-2"></i>;
    return ordersFilters.sortOrder === 'asc'
      ? <i className="fa-solid fa-sort-up text-blue-600 dark:text-blue-400 ml-2"></i>
      : <i className="fa-solid fa-sort-down text-blue-600 dark:text-blue-400 ml-2"></i>;
  };

  const getStatusBadgeVariant = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
    switch (status.toLowerCase()) {
      case 'submitted': return 'info';
      case 'inapproval':
      case 'in approval': return 'warning';
      case 'approved':
      case 'completed': return 'success';
      case 'rejected':
      case 'cancelled': return 'error';
      case 'paused': return 'warning';
      case 'needsresolved':
      case 'needs resolved': return 'error';
      default: return 'default';
    }
  };

  const formatStatus = (status: string) =>
    status.replace(/([A-Z])/g, ' $1').trim().replace(/^./, s => s.toUpperCase());

  const matchingModeLabel = (mode?: string) => {
    if (mode === 'routingRules') return 'Routing Rules';
    if (mode === 'matchIdentifiers') return 'Match Identifiers';
    return 'None';
  };

  const tabs = [
    {
      id: 'basic',
      label: 'Basic Info',
      content: (
        <div className="space-y-6 max-w-2xl">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-secondary-400 mb-1">Workflow Name</label>
              <p className="text-sm text-gray-900 dark:text-white font-medium">{workflow?.name}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-secondary-400 mb-1">Workflow Type</label>
              <p className="text-sm text-gray-900 dark:text-white capitalize">{workflow?.type}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-secondary-400 mb-1">Status</label>
              <Badge variant={workflow?.active ? 'success' : 'default'}>
                {workflow?.active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-secondary-400 mb-1">Matching Mode</label>
              <p className="text-sm text-gray-900 dark:text-white">{matchingModeLabel(workflow?.matchingMode)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-secondary-400 mb-1">Created</label>
              <p className="text-sm text-gray-900 dark:text-white">{workflow?.createdAt ? formatDate(workflow.createdAt) : '—'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-secondary-400 mb-1">Last Modified</label>
              <p className="text-sm text-gray-900 dark:text-white">{workflow?.updatedAt ? formatDate(workflow.updatedAt) : '—'}</p>
            </div>
          </div>

          {workflow?.description && (
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-secondary-400 mb-1">Description</label>
              <p className="text-sm text-gray-900 dark:text-white">{workflow.description}</p>
            </div>
          )}

          {/* Settings */}
          {workflow?.settings && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-secondary-200 uppercase tracking-wide mb-3 pt-4 border-t border-gray-100 dark:border-secondary-700">
                Settings
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-secondary-400 mb-1">Auto Escalation</label>
                  <p className="text-sm text-gray-900 dark:text-white">{workflow.settings.autoEscalation ? 'Enabled' : 'Disabled'}</p>
                </div>
                {workflow.settings.autoEscalation && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-secondary-400 mb-1">Escalation After</label>
                    <p className="text-sm text-gray-900 dark:text-white">{workflow.settings.escalationHours}h</p>
                  </div>
                )}
                {workflow.settings.defaultStepTimeoutHours != null && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-secondary-400 mb-1">Default Step Timeout</label>
                    <p className="text-sm text-gray-900 dark:text-white">{workflow.settings.defaultStepTimeoutHours}h</p>
                  </div>
                )}
                {workflow.settings.pauseTimeoutHours != null && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-secondary-400 mb-1">Pause Timeout</label>
                    <p className="text-sm text-gray-900 dark:text-white">{workflow.settings.pauseTimeoutHours}h</p>
                  </div>
                )}
                {workflow.settings.defaultTimeoutWarningHours != null && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-secondary-400 mb-1">Timeout Warning</label>
                    <p className="text-sm text-gray-900 dark:text-white">{workflow.settings.defaultTimeoutWarningHours}h before timeout</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-secondary-400 mb-1">Email Notifications</label>
                  <p className="text-sm text-gray-900 dark:text-white">{workflow.settings.notifications.email ? 'Enabled' : 'Disabled'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-secondary-400 mb-1">Timeout Action</label>
                  <p className="text-sm text-gray-900 dark:text-white capitalize">{workflow.settings.timeoutAction}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'workflow-steps',
      label: 'Workflow Steps',
      content: (
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold">Approval Levels</h3>
            <p className="text-sm text-gray-600 dark:text-secondary-400 mt-1">
              Approvers and conditions configured for each step in this workflow
            </p>
          </div>

          {!workflow?.steps?.length ? (
            <div className="text-center py-8 text-gray-500 dark:text-secondary-400">No workflow steps configured.</div>
          ) : (
            <div className="space-y-4">
              {workflow.steps.map((step) => {
                const approvers = step.approvers;
                const roleIds = approvers.divisionRoleIds ?? [];
                const userIds = approvers.userIds ?? [];
                const inviteEmails = approvers.inviteEmails ?? [];
                const matchIds = approvers.matchIdentifiers ?? [];

                return (
                  <div key={step.step} className="border border-gray-200 dark:border-secondary-700 rounded-lg p-5 space-y-4 bg-white dark:bg-secondary-800">
                    {/* Level header */}
                    <div className="flex items-center gap-3">
                      <Badge variant="info" className="shrink-0">Level {step.step}</Badge>
                      <span className="font-semibold text-gray-900 dark:text-white text-base">{step.name}</span>
                      {step.stepType && step.stepType !== 'approval' && (
                        <Badge variant="default">{getStepTypeLabel(step.stepType)}</Badge>
                      )}
                    </div>

                    {step.description && (
                      <p className="text-sm text-gray-600 dark:text-secondary-400">{step.description}</p>
                    )}

                    {/* Approver configuration grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wide mb-1">Approver Type</label>
                        <p className="text-gray-900 dark:text-white capitalize">{approvers.type.replace(/_/g, ' ')}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wide mb-1">Required Approvals</label>
                        <p className="text-gray-900 dark:text-white">{approvers.requiredApprovals}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wide mb-1">Parallel Approval</label>
                        <p className="text-gray-900 dark:text-white">{approvers.parallelApproval ? 'Yes' : 'No'}</p>
                      </div>
                      {approvers.timeoutHours != null && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wide mb-1">Timeout</label>
                          <p className="text-gray-900 dark:text-white">{approvers.timeoutHours}h</p>
                        </div>
                      )}
                      {approvers.timeoutWarningHours != null && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wide mb-1">Timeout Warning</label>
                          <p className="text-gray-900 dark:text-white">{approvers.timeoutWarningHours}h before</p>
                        </div>
                      )}
                    </div>

                    {/* Division Roles */}
                    {roleIds.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wide mb-2">Division Roles</label>
                        <div className="flex flex-wrap gap-2">
                          {roleIds.map((id) => (
                            <Badge key={id} variant="info">{getRoleName(id)}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Specific Users */}
                    {userIds.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wide mb-2">Specific Approvers</label>
                        <div className="space-y-1">
                          {userIds.map((id) => (
                            <p key={id} className="text-sm text-gray-900 dark:text-white">{getUserLabel(id)}</p>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Invite Emails */}
                    {inviteEmails.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wide mb-2">Invited Approvers</label>
                        <div className="space-y-1">
                          {inviteEmails.map((email) => (
                            <p key={email} className="text-sm text-gray-900 dark:text-white">{email}</p>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Match Identifier-based */}
                    {matchIds.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wide mb-2">Match Identifier Approvers</label>
                        <div className="flex flex-wrap gap-2">
                          {matchIds.map((id) => (
                            <Badge key={id} variant="default">{id}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Conditions */}
                    {step.conditions?.amountThreshold != null && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wide mb-1">Amount Threshold</label>
                        <p className="text-sm text-gray-900 dark:text-white">${step.conditions.amountThreshold.toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'routing-rules',
      label: 'Routing Rules',
      content: (
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold">Order Routing Rules</h3>
            <p className="text-sm text-gray-600 dark:text-secondary-400 mt-1">
              Conditions used to match orders to this workflow
            </p>
          </div>

          {!workflow?.routingRules?.length ? (
            <div className="text-center py-8 text-gray-500 dark:text-secondary-400">No routing rules configured.</div>
          ) : (
            <div className="border border-gray-200 dark:border-secondary-700 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-secondary-700 text-sm">
                <thead className="bg-gray-50 dark:bg-secondary-900">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-secondary-400">Field Path</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-secondary-400">Operator</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-secondary-400">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white dark:bg-secondary-800">
                  {workflow.routingRules.map((rule, i) => (
                    <tr key={rule.id ?? i} className="hover:bg-gray-50 dark:hover:bg-secondary-700">
                      <td className="px-4 py-3 font-mono text-gray-900 dark:text-white">{rule.field}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-secondary-200 capitalize">{rule.operator}</td>
                      <td className="px-4 py-3 text-gray-900 dark:text-white">
                        {rule.valueEnd ? `${rule.value} – ${rule.valueEnd}` : rule.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'order-attributes',
      label: 'Order Attributes',
      content: (
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold">Order Attributes</h3>
            <p className="text-sm text-gray-600 dark:text-secondary-400 mt-1">
              Order fields that can be edited during approval
            </p>
          </div>

          {isLoadingFields ? (
            <div className="text-center py-8">Loading order attributes...</div>
          ) : !orderAttributesResponse?.length ? (
            <div className="text-center py-8 text-gray-500 dark:text-secondary-400">No order attributes configured.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Field Label</TableHead>
                  <TableHead>JSON Path</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Required</TableHead>
                  <TableHead>Validation</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orderAttributesResponse.map((field: any) => (
                  <TableRow key={field.id}>
                    <TableCell className="font-medium">{field.label}</TableCell>
                    <TableCell className="font-mono text-sm">{field.jsonPath}</TableCell>
                    <TableCell>
                      <Badge variant="info">{field.fieldType}</Badge>
                    </TableCell>
                    <TableCell>
                      {field.isRequired ? (
                        <Badge variant="default">Required</Badge>
                      ) : (
                        <Badge variant="info">Optional</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {field.validationRules ? (
                        <Badge variant="info">Has Rules</Badge>
                      ) : (
                        <span className="text-gray-400 dark:text-secondary-500">None</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      ),
    },
    {
      id: 'orders',
      label: 'Orders',
      content: (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Orders Using This Workflow</h3>
              <p className="text-sm text-gray-600 dark:text-secondary-400 mt-1">All orders processed through this workflow</p>
            </div>
            <Badge variant="info">{ordersResponse?.totalCount || 0} total</Badge>
          </div>

          <div className="bg-white dark:bg-secondary-800 rounded-lg border border-gray-200 dark:border-secondary-700 p-4 space-y-4">
            <input
              type="text"
              placeholder="Search by order number..."
              value={ordersSearchInput}
              onChange={(e) => setOrdersSearchInput(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
            />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">Status</label>
                <select
                  value={ordersFilters.status}
                  onChange={(e) => setOrdersFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                >
                  <option value="">All Statuses</option>
                  <option value="Submitted">Submitted</option>
                  <option value="InApproval">In Approval</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Paused">Paused</option>
                  <option value="NeedsResolved">Needs Resolved</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">Sort By</label>
                <select
                  value={ordersFilters.sortBy}
                  onChange={(e) => setOrdersFilters(prev => ({ ...prev, sortBy: e.target.value, page: 1 }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                >
                  <option value="CreatedAt">Created Date</option>
                  <option value="UpdatedAt">Updated Date</option>
                  <option value="OrderNumber">Order Number</option>
                  <option value="TotalAmount">Amount</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">Sort Order</label>
                <select
                  value={ordersFilters.sortOrder}
                  onChange={(e) => setOrdersFilters(prev => ({ ...prev, sortOrder: e.target.value as 'asc' | 'desc', page: 1 }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">Page Size</label>
                <select
                  value={ordersFilters.limit}
                  onChange={(e) => setOrdersFilters(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                >
                  <option value="25">25 per page</option>
                  <option value="50">50 per page</option>
                  <option value="100">100 per page</option>
                </select>
              </div>
            </div>
          </div>

          {isLoadingOrders ? (
            <div className="text-center py-8">Loading orders...</div>
          ) : !ordersResponse?.items?.length ? (
            <div className="text-center py-8 text-gray-500 dark:text-secondary-400">
              {ordersFilters.search || ordersFilters.status
                ? 'No orders found matching your filters.'
                : 'No orders have been processed through this workflow yet.'}
            </div>
          ) : (
            <>
              <div className="bg-white dark:bg-secondary-800 rounded-lg border border-gray-200 dark:border-secondary-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-secondary-700">
                    <thead className="bg-gray-50 dark:bg-secondary-900">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600" {...sortableHeaderProps(() => handleOrdersSort('OrderNumber'), ordersFilters.sortBy === 'OrderNumber', ordersFilters.sortOrder)}>
                          Order Number{getOrdersSortIcon('OrderNumber')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">Submitter</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600" {...sortableHeaderProps(() => handleOrdersSort('TotalAmount'), ordersFilters.sortBy === 'TotalAmount', ordersFilters.sortOrder)}>
                          Total{getOrdersSortIcon('TotalAmount')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600" {...sortableHeaderProps(() => handleOrdersSort('CreatedAt'), ordersFilters.sortBy === 'CreatedAt', ordersFilters.sortOrder)}>
                          Created{getOrdersSortIcon('CreatedAt')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600" {...sortableHeaderProps(() => handleOrdersSort('UpdatedAt'), ordersFilters.sortBy === 'UpdatedAt', ordersFilters.sortOrder)}>
                          Updated{getOrdersSortIcon('UpdatedAt')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-secondary-800 divide-y divide-gray-200 dark:divide-secondary-700">
                      {ordersResponse.items.map((order: any) => (
                        <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-secondary-700">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              className="font-mono text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 hover:underline font-medium"
                              onClick={() => navigate(`/order-detail/${order.id}`)}
                            >
                              {order.orderNumber}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">{order.submitterName || 'Unknown'}</div>
                            {order.submitterEmail && <div className="text-sm text-gray-500 dark:text-secondary-400">{order.submitterEmail}</div>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge variant={getStatusBadgeVariant(order.status)}>{formatStatus(order.status)}</Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {order.originalTotalAmount && order.originalTotalAmount !== order.totalAmount ? (
                              <div className="flex flex-col items-start gap-0.5">
                                <span className="line-through text-gray-400 dark:text-secondary-500 text-xs">{formatCurrency(order.originalTotalAmount, order.currency)}</span>
                                <span className="text-blue-600 dark:text-blue-400 font-semibold text-sm">{formatCurrency(order.totalAmount, order.currency)}</span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-900 dark:text-white">{formatCurrency(order.totalAmount, order.currency)}</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-secondary-400">{formatDate(order.createdAt)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-secondary-400">{formatDate(order.updatedAt)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/order-detail/${order.id}`)} title="View Order Details">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {ordersResponse.totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700 dark:text-secondary-200">
                    Showing {((ordersResponse.page - 1) * ordersResponse.pageSize) + 1} to{' '}
                    {Math.min(ordersResponse.page * ordersResponse.pageSize, ordersResponse.totalCount)} of{' '}
                    {ordersResponse.totalCount} results
                  </div>
                  <div className="flex space-x-2">
                    <Button onClick={() => setOrdersFilters(prev => ({ ...prev, page: prev.page - 1 }))} disabled={ordersResponse.page === 1} variant="outline" size="sm">
                      <i className="fa-solid fa-chevron-left mr-2"></i> Previous
                    </Button>
                    <div className="flex items-center space-x-2">
                      {Array.from({ length: Math.min(5, ordersResponse.totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (ordersResponse.totalPages <= 5) pageNum = i + 1;
                        else if (ordersResponse.page <= 3) pageNum = i + 1;
                        else if (ordersResponse.page >= ordersResponse.totalPages - 2) pageNum = ordersResponse.totalPages - 4 + i;
                        else pageNum = ordersResponse.page - 2 + i;
                        return (
                          <Button key={pageNum} onClick={() => setOrdersFilters(prev => ({ ...prev, page: pageNum }))} variant={ordersResponse.page === pageNum ? 'primary' : 'outline'} size="sm">
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button onClick={() => setOrdersFilters(prev => ({ ...prev, page: prev.page + 1 }))} disabled={ordersResponse.page === ordersResponse.totalPages} variant="outline" size="sm">
                      Next <i className="fa-solid fa-chevron-right ml-2"></i>
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ),
    },
    {
      id: 'audit',
      label: 'Audit History',
      content: (
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold mb-2">Workflow Change History</h3>
            <p className="text-sm text-gray-600 dark:text-secondary-400 mb-6">Complete audit trail of all changes made to this workflow</p>
          </div>
          <WorkflowAuditTrail divisionId={divisionId as string} workflowId={workflowId as string} />
        </div>
      ),
    },
  ];

  if (!isAuthenticated) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-lg text-red-600 dark:text-red-400">Please log in to view workflows.</div>
        </div>
      </SidebarLayout>
    );
  }

  if (isLoading) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-lg">Loading workflow...</div>
        </div>
      </SidebarLayout>
    );
  }

  if (!workflow) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-lg text-red-600 dark:text-red-400">Workflow not found</div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="p-6">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate(`/workflows/${divisionId}`)} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Workflows
          </Button>

          <div className="flex justify-between items-center">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">Workflow Details</h1>
                <Badge variant={workflow.active ? 'success' : 'default'}>
                  {workflow.active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <p className="text-gray-600 dark:text-secondary-400 mt-1">{workflow.name}</p>
            </div>

            {!isSupport && (
              <Button onClick={() => navigate(`/workflows/${divisionId}/${workflowId}/edit`)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit Workflow
              </Button>
            )}
          </div>
        </div>

        <Tabs tabs={tabs} activeTabId={activeTab} onTabChange={handleTabChange} />
      </div>
    </SidebarLayout>
  );
}
